import Highcharts from 'highcharts'
import moment from 'moment'

// Same module-level side effects as legacy src/components/elements/Chart/Chart.tsx,
// but run once at import time instead of on every component render: registers the
// accessibility module and pins the timezone used by every chart in the app.
// eslint-disable-next-line @typescript-eslint/no-var-requires
require('highcharts/modules/accessibility')(Highcharts)

Highcharts.setOptions({
  time: {
    timezone: 'Europe/Rome',
    useUTC: false,
  },
})

export type ChartPresetType = 'pie' | 'spline' | 'line' | 'area' | 'column' | 'stacking-column' | 'nyha' | 'morisky'

export interface BuildChartOptionsParams {
  type: ChartPresetType
  data: any
  colors: string[]
  title?: string
  legend?: boolean
  percentage?: boolean
  userGroup?: string
}

// Replicates the legacy Chart component's "any data at all?" check
// ((Array.isArray(data) && data.length > 0) || (Array.isArray(data.series) && data.series.length > 0))
// used to decide between rendering the chart or an empty-state message.
export function hasChartData(data: any): boolean {
  return (Array.isArray(data) && data.length > 0) || (Array.isArray(data?.series) && data.series.length > 0)
}

const pieDataLabelFormat = (legend: boolean, userGroup: string): string => {
  if (userGroup.toUpperCase().includes('DOCTOR')) {
    return legend ? '{point.y}' : '{point.name} {point.y}'
  } else return legend ? '{point.percentage:.1f} % ({point.y})' : '{point.name} {point.percentage:.1f} % ({point.y})'
}

const pieOptions = (legend: boolean, userGroup: string): Highcharts.Options => ({
  chart: {
    plotShadow: false,
    type: 'pie',
  },
  title: {
    text: '',
  },
  tooltip: {
    enabled: false,
  },
  accessibility: {
    point: {
      valueSuffix: '%',
    },
  },
  plotOptions: {
    series: {
      animation: false,
    },
    pie: {
      allowPointSelect: false,
      cursor: 'pointer',
      showInLegend: legend,
      dataLabels: {
        enabled: true,
        format: pieDataLabelFormat(legend, userGroup),
        distance: 30,
        filter: {
          operator: '>',
          property: 'y',
          value: 0,
        },
        style: {
          color: 'black',
          textOutline: 'none',
          fontSize: '12px',
          fontWeight: '900',
        },
      },
    },
  },
  legend: {
    align: 'right',
    layout: 'vertical',
    verticalAlign: 'top',
  },
  credits: {
    enabled: false,
  },
})

const splineOptions = (userGroup: string): Highcharts.Options => ({
  chart: {
    type: 'spline',
  },
  title: {
    text: '',
  },
  xAxis: {
    type: 'datetime',
    dateTimeLabelFormats: {
      millisecond: '%H:%M:%S.%L',
      second: '%H:%M:%S',
      minute: '%H:%M',
      hour: '%H:%M',
      day: '%e/%m/%y',
      week: '%e/%m/%y',
      month: '%m/%y',
      year: '%Y',
    },
  },
  tooltip: {
    dateTimeLabelFormats: {
      millisecond: '%e/%m/%y %H:%M:%S',
      second: '%e/%m/%y %H:%M:%S',
      minute: '%e/%m/%y %H:%M',
      hour: '%e/%m/%y %H:%M',
      day: '%e/%m/%y %H:%M',
      week: '%e/%m/%y %H:%M',
      month: '%m/%y %H:%M',
      year: '%m/%y %H:%M',
    },
    valueDecimals: userGroup.toUpperCase().includes('DOCTOR') ? 0 : 1,
  },
  yAxis: {
    title: {
      text: '',
    },
  },
  legend: {
    layout: 'vertical',
    align: 'right',
    verticalAlign: 'middle',
  },
  plotOptions: {
    series: {
      marker: {
        symbol: 'circle',
        enabled: true,
        radius: 3,
        lineWidth: 2,
        lineColor: undefined,
      },
    },
  },
  credits: {
    enabled: false,
  },
})

const areaOptions = (userGroup: string): Highcharts.Options => ({
  chart: {
    renderTo: 'container',
    type: 'area',
  },
  title: {
    text: '',
  },
  tooltip: {
    enabled: false,
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    type: 'category',
    labels: {
      rotation: 0,
      style: {
        color: 'black',
        textOutline: 'none',
        fontSize: '12px',
        fontWeight: '400',
      },
    },
  },
  yAxis: {
    title: {
      text: '',
    },
  },
  plotOptions: {
    series: {
      animation: false,
    },
    area: {
      lineWidth: 5,
      marker: {
        radius: 6,
      },
      dataLabels: {
        enabled: true,
        style: {
          color: 'black',
          textOutline: 'none',
          fontSize: '12px',
          fontWeight: '100',
        },
        format: userGroup.toUpperCase().includes('DOCTOR') ? '{point.y:.0f}' : '{point.y:.1f}',
      },
    },
  },
  credits: {
    enabled: false,
  },
})

const columnOptions = (userGroup: string): Highcharts.Options => ({
  chart: {
    renderTo: 'container',
    type: 'column',
  },
  tooltip: {
    enabled: true,
  },
  title: {
    text: '',
  },
  subtitle: {
    text: '',
    align: 'center',
    verticalAlign: 'bottom',
    style: {
      fontSize: '14px',
      fontWeight: '700',
      color: 'black',
    },
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    type: 'category',
    labels: {
      rotation: 0,
      style: {
        fontSize: '12px',
        fontWeight: '400',
        lineWidth: 1,
      },
    },
  },
  yAxis: {
    title: {
      text: '',
    },
  },
  credits: {
    enabled: false,
  },
})

const stackingColumnOptions = (userGroup: string): Highcharts.Options => ({
  chart: {
    renderTo: 'container',
    type: 'column',
  },
  tooltip: {
    enabled: false,
  },
  title: {
    text: '',
  },
  subtitle: {
    text: '',
    align: 'center',
    verticalAlign: 'bottom',
    style: {
      fontSize: '14px',
      fontWeight: '700',
      color: 'black',
    },
  },
  legend: {
    enabled: true,
  },
  plotOptions: {
    series: {
      animation: false,
    },
    column: {
      stacking: 'normal',
      dataLabels: {
        enabled: true,
        inside: true,
      },
    },
  },
  credits: {
    enabled: false,
  },
})

// Pure port of legacy src/components/elements/Chart/Chart.tsx's per-render effect body:
// same options per preset type, same data-labels formatting, same "Totale ..." subtitle,
// same NYHA percent-stacking — but computed directly instead of via useState/useEffect,
// since there is no reason here to defer the computation to a render pass.
export function buildChartOptions({
  type,
  data,
  colors,
  title,
  legend = true,
  percentage = false,
  userGroup = '',
}: BuildChartOptionsParams): Highcharts.Options {
  if (type === 'pie') {
    return {
      ...pieOptions(legend, userGroup),
      colors,
      series: [{
        type: 'pie',
        data,
      }],
    }
  }

  if (type === 'spline') {
    return {
      ...splineOptions(userGroup),
      colors,
      series: data.series,
    }
  }

  if (type === 'line') {
    return {
      ...splineOptions(userGroup),
      title: {
        text: title,
      },
      legend: {
        enabled: legend,
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.y:.2f}</b>',
      },
      plotOptions: {
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 2,
            lineColor: undefined,
          },
          dataLabels: {
            enabled: true,
            format: '{point.y:.2f}',
          },
        },
      },
      xAxis: {
        ...splineOptions(userGroup).xAxis,
        categories: data.xAxis,
      },
      yAxis: {
        ...splineOptions(userGroup).yAxis,
        min: 0,
      },
      colors,
      series: data.series,
    }
  }

  if (type === 'area') {
    return {
      ...areaOptions(userGroup),
      colors,
      series: [{
        type: 'area',
        data,
      }],
    }
  }

  if (type === 'column') {
    if (Array.isArray(data)) {
      return {
        ...columnOptions(userGroup),
        plotOptions: {
          series: {
            animation: false,
          },
          column: {
            pointPadding: 0,
            borderWidth: 0,
            dataLabels: {
              inside: true,
              enabled: true,
              format: percentage ? (userGroup.toUpperCase().includes('DOCTOR') ? '{y:.0f}%' : '{y:.1f}%') : undefined,
            },
          },
        },
        subtitle: {
          ...columnOptions(userGroup).subtitle,
          text: `Totale ${data.reduce((sum: number, point: any) => sum + point.y, 0)}`,
        },
        colors,
        series: [{
          type: 'column',
          data,
        }],
      }
    }

    const series = percentage && data.series ? data.series.map((el: any) => ({
      name: el.name,
      data: el.data.map((value: number) => Math.round((value * 100 + Number.EPSILON) * 100) / 100),
      color: el.color,
    })) : data.series

    return {
      ...columnOptions(userGroup),
      plotOptions: {
        series: {
          animation: false,
        },
        column: {
          pointPadding: 0,
          borderWidth: 0,
          dataLabels: {
            inside: true,
            enabled: true,
            allowOverlap: true,
            padding: 0,
            format: percentage ? (userGroup.toUpperCase().includes('DOCTOR') ? '{y:.0f}%' : '{y:.1f}%') : undefined,
          },
        },
      },
      title: {
        text: title,
      },
      legend: {
        enabled: true,
      },
      xAxis: {
        ...columnOptions(userGroup).xAxis,
        categories: data.xAxis,
      },
      colors,
      series,
    }
  }

  if (type === 'stacking-column') {
    const series = percentage && data.series ? data.series.map((el: any) => ({
      name: el.name,
      data: el.data.map((value: number) => Math.round((value * 100 + Number.EPSILON) * 10) / 10),
      color: el.color,
    })) : data.series

    return {
      ...stackingColumnOptions(userGroup),
      xAxis: {
        categories: data.xAxis?.categories ? data.xAxis.categories : data.xAxis,
      },
      yAxis: {
        title: {
          text: '',
        },
        stackLabels: {
          enabled: true,
          format: percentage ? '{total}%' : undefined,
        },
      },
      colors,
      subtitle: {
        ...stackingColumnOptions(userGroup).subtitle,
      },
      series,
      plotOptions: percentage ? {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            format: userGroup.toUpperCase().includes('DOCTOR') ? '{y:.0f} %' : '{y:.1f} %',
          },
        },
      } : undefined,
    }
  }

  if (type === 'nyha') {
    return {
      ...stackingColumnOptions(userGroup),
      xAxis: {
        categories: data.xAxis?.categories,
      },
      yAxis: {
        title: {
          text: '',
        },
        max: 100,
      },
      colors,
      subtitle: {
        ...stackingColumnOptions(userGroup).subtitle,
      },
      series: data.series,
      plotOptions: {
        column: {
          stacking: 'percent',
          dataLabels: {
            enabled: true,
            format: '{percentage:.1f}%',
            filter: {
              property: 'percentage',
              operator: '>',
              value: 0,
            },
          },
        },
      },
    }
  }

  // type === 'morisky'
  const xAxisLabels = [
    moment().subtract(3, 'months').format('MMM YYYY'),
    moment().subtract(2, 'months').format('MMM YYYY'),
    moment().subtract(1, 'months').format('MMM YYYY'),
    moment().format('MMM YYYY'),
  ]
  return {
    chart: {
      renderTo: 'container',
      type: 'column',
    },
    title: {
      text: title,
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      categories: xAxisLabels,
    },
    yAxis: {
      min: 0,
    },
    colors,
    plotOptions: {
      series: {
        animation: false,
      },
      column: {
        dataLabels: {
          enabled: true,
          style: {
            color: 'black',
            textOutline: 'none',
            fontSize: '12px',
            fontWeight: '100',
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [{
      type: 'column',
      data,
    }],
  }
}
